import { state } from './state.js';
import * as API from './api.js';
import { renderRecentPhotos, setRecentPhotoSelection, upsertRecentPhoto } from './ui/source_photos.js';
import { setOriginalSize } from './target_controller.js';
import { refreshSettingsVisibility, setSettingsOverride } from './workflow_selection.js';
import { openConfirmModal } from './modal_manager.js';
import { showToast } from './ui/toast.js';

const RECENT_PHOTOS_LIMIT = 24;
const SOURCE_PREVIEW_MIN_SIZE = 128;
const SOURCE_PREVIEW_MAX_SIZE = 1800;
const SOURCE_PREVIEW_FALLBACK_SIZE = 512;
const SOURCE_PREVIEW_SWITCH_ANIM_MS = 800;
const SOURCE_PREVIEW_RESIZE_ANIM_MS = 300;
let _sourcePreviewSwapToken = 0;
let _sourcePreviewSwapTimer = 0;
let _sourcePreviewSwapIncoming = null;
let _sourcePreviewResizeTimer = 0;

function _prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
        return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_e) {
        return false;
    }
}

function _resetSourcePreviewSwap(preview) {
    if (_sourcePreviewSwapTimer) {
        window.clearTimeout(_sourcePreviewSwapTimer);
        _sourcePreviewSwapTimer = 0;
    }
    if (_sourcePreviewSwapIncoming && _sourcePreviewSwapIncoming.parentNode) {
        _sourcePreviewSwapIncoming.parentNode.removeChild(_sourcePreviewSwapIncoming);
    }
    _sourcePreviewSwapIncoming = null;
    if (preview) {
        preview.classList.remove('source-preview-swap-transition');
        preview.classList.remove('source-preview-swap-out');
        preview.classList.remove('source-preview-swap-out-active');
    }
}

function _getMainPhotoPlaceholderHtml() {
    const hasUploadedPhotos = Boolean(
        Array.isArray(state.sourcePhotos) &&
        state.sourcePhotos.length > 0
    );
    const line = hasUploadedPhotos
        ? 'Upload a Main Photo or choose it from Uploaded Photos.'
        : 'Upload a Main Photo.';
    return `
        <div style="font-size:34px; margin-bottom:10px;">📷</div>
        <div><b>${line}</b></div>
    `;
}

function _refreshMainPhotoPlaceholderCopy() {
    const preview = document.getElementById('sourcePreview');
    const placeholder = document.getElementById('targetDropLabel');
    if (!placeholder) return;
    const hasPreview = Boolean(preview && !preview.classList.contains('hidden') && preview.getAttribute('src'));
    if (hasPreview) return;
    placeholder.innerHTML = _getMainPhotoPlaceholderHtml();
}

function _setSourceWorkspaceVisibility() {
    const stripSlot = document.querySelector('.main-photo-library-slot');
    const previewSlot = document.querySelector('.main-photo-preview-slot');
    const showStrip = Boolean(
        Array.isArray(state.sourcePhotos) &&
        state.sourcePhotos.length > 0
    );
    if (stripSlot) stripSlot.classList.toggle('hidden', !showStrip);
    if (previewSlot) previewSlot.classList.toggle('full-width', !showStrip);
}

function _computeMainPreviewSize() {
    const dropZone = document.getElementById('targetDrop');
    const width = Number(dropZone && dropZone.clientWidth) || 0;
    const height = Number(dropZone && dropZone.clientHeight) || 0;
    const cssLongEdge = Math.max(width, height);
    const dpr = Math.max(1, Number(window.devicePixelRatio || 1));
    const base = cssLongEdge > 0 ? Math.ceil(cssLongEdge * dpr) : SOURCE_PREVIEW_FALLBACK_SIZE;
    return Math.max(SOURCE_PREVIEW_MIN_SIZE, Math.min(SOURCE_PREVIEW_MAX_SIZE, base));
}

function _buildSourcePreviewUrl(sourceId) {
    const safeId = String(sourceId || '').trim();
    if (!safeId) return '';
    const size = _computeMainPreviewSize();
    return `/source-thumb?source_id=${encodeURIComponent(safeId)}&size=${size}`;
}

function _resolveMainPreviewUrl(source) {
    if (!source) return '';
    if (typeof source === 'string') return source;
    if (typeof source === 'object') {
        const sourceId = String(source.id || '');
        const byId = sourceId && !sourceId.startsWith('legacy:')
            ? _buildSourcePreviewUrl(sourceId)
            : '';
        if (byId) return byId;
        const thumb = String(source.thumb_url || '').trim();
        if (thumb) return thumb;
        const full = String(source.full_url || source.source_url || '').trim();
        if (full) return full;
        return '';
    }
    return '';
}

function _applyDropAspectFromDims(dropZone, width, height) {
    const w = Number(width || 0);
    const h = Number(height || 0);
    if (!dropZone || !w || !h) return;
    const ratio = w / h;
    dropZone.style.aspectRatio = `${w} / ${h}`;
    dropZone.style.setProperty('--source-ratio', String(ratio));
}

function _setDropBackdropImage(dropZone, imageUrl) {
    if (!dropZone) return;
    const safeUrl = String(imageUrl || '').trim();
    if (!safeUrl) {
        dropZone.style.removeProperty('--source-preview-backdrop');
        dropZone.classList.remove('has-source-preview-backdrop');
        return;
    }
    const escapedUrl = safeUrl
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');
    dropZone.style.setProperty('--source-preview-backdrop', `url("${escapedUrl}")`);
    dropZone.classList.add('has-source-preview-backdrop');
}

function _clearDropResizeAnimation(dropZone) {
    if (_sourcePreviewResizeTimer) {
        window.clearTimeout(_sourcePreviewResizeTimer);
        _sourcePreviewResizeTimer = 0;
    }
    if (dropZone) {
        dropZone.classList.remove('source-preview-resizing');
    }
}

function _startDropResizeAnimation(dropZone) {
    if (!dropZone) return;
    dropZone.classList.add('source-preview-resizing');
    if (_sourcePreviewResizeTimer) {
        window.clearTimeout(_sourcePreviewResizeTimer);
        _sourcePreviewResizeTimer = 0;
    }
    _sourcePreviewResizeTimer = window.setTimeout(() => {
        if (dropZone) dropZone.classList.remove('source-preview-resizing');
        _sourcePreviewResizeTimer = 0;
    }, SOURCE_PREVIEW_RESIZE_ANIM_MS + 32);
}

function _upsertSourcePhotoState(photo) {
    if (!photo || !photo.id) return;
    const normalized = { ...photo, has_been_used: Boolean(photo.has_been_used) };
    const items = Array.isArray(state.sourcePhotos) ? [...state.sourcePhotos] : [];
    const existingIndex = items.findIndex((p) => p.id === normalized.id);
    if (existingIndex >= 0) items.splice(existingIndex, 1);
    items.unshift(normalized);
    state.sourcePhotos = items;
}

function _setDropPreview(source) {
    const sourceUrl = _resolveMainPreviewUrl(source);
    const preview = document.getElementById('sourcePreview');
    const placeholder = document.getElementById('targetDropLabel');
    const overlay = document.getElementById('targetDropOverlay');
    const dropZone = document.getElementById('targetDrop');
    const sourceId = String(source && typeof source === 'object' ? source.id || '' : '');
    const fullSourceUrl = String(
        source && typeof source === 'object'
            ? (source.full_url || source.source_url || '')
            : ''
    );
    const sourceWidth = Number(source && typeof source === 'object' ? source.width : 0);
    const sourceHeight = Number(source && typeof source === 'object' ? source.height : 0);
    const hasKnownDims = Number.isFinite(sourceWidth) && Number.isFinite(sourceHeight) && sourceWidth > 0 && sourceHeight > 0;
    const shouldProbeFullDims = !hasKnownDims && Boolean(fullSourceUrl) && fullSourceUrl !== sourceUrl;

    if (preview) {
        const applyResolvedSizing = (width, height, options = {}) => {
            const w = Number(width || 0);
            const h = Number(height || 0);
            if (!dropZone || !w || !h) return;
            const animateResize = Boolean(options.animateResize);
            if (animateResize) _startDropResizeAnimation(dropZone);
            _applyDropAspectFromDims(dropZone, w, h);
            setOriginalSize(w, h);
        };
        const applyKnownSizing = (options = {}) => {
            if (!hasKnownDims) return;
            applyResolvedSizing(sourceWidth, sourceHeight, options);
        };
        const applyNaturalSizingFromImage = (imageEl, options = {}) => {
            if (hasKnownDims) return;
            if (shouldProbeFullDims) return;
            try {
                const w = Number(imageEl && imageEl.naturalWidth ? imageEl.naturalWidth : 0);
                const h = Number(imageEl && imageEl.naturalHeight ? imageEl.naturalHeight : 0);
                if (!w || !h) return;
                applyResolvedSizing(w, h, options);
            } catch (e) {}
        };
        const nextSrc = sourceUrl || '';
        const probeFullSourceDims = (options = {}) => {
            if (!shouldProbeFullDims) return;
            const expectedSourceId = sourceId || String(state.selectedSourcePhotoId || '');
            const expectedPreviewSrc = nextSrc;
            const probe = new Image();
            probe.onload = () => {
                if (expectedSourceId && String(state.selectedSourcePhotoId || '') !== expectedSourceId) return;
                if ((preview.getAttribute('src') || '') !== expectedPreviewSrc) return;
                const w = Number(probe.naturalWidth || 0);
                const h = Number(probe.naturalHeight || 0);
                if (w > 0 && h > 0) {
                    applyResolvedSizing(w, h, options);
                }
            };
            probe.onerror = () => {};
            probe.src = fullSourceUrl;
        };
        preview.onload = () => {
            applyNaturalSizingFromImage(preview);
            preview.classList.remove('img-loading');
            if (dropZone) dropZone.classList.remove('is-loading');
        };
        preview.onerror = () => {
            preview.classList.remove('img-loading');
            if (dropZone) dropZone.classList.remove('is-loading');
        };
        const currentSrc = preview.getAttribute('src') || '';
        const toSourceId = sourceId || String(source && typeof source === 'object' ? source.id || '' : '');
        const fromSourceId = String(preview.dataset.sourceId || '');
        const shouldAnimateSwap = Boolean(
            nextSrc &&
            currentSrc &&
            currentSrc !== nextSrc &&
            !preview.classList.contains('hidden') &&
            !_prefersReducedMotion()
        );

        if (shouldAnimateSwap && dropZone) {
            const token = ++_sourcePreviewSwapToken;
            _resetSourcePreviewSwap(preview);
            const incoming = document.createElement('img');
            incoming.className = 'source-preview-image source-preview-swap-incoming';
            incoming.alt = preview.alt || '';
            incoming.decoding = 'async';
            incoming.loading = 'eager';
            _sourcePreviewSwapIncoming = incoming;
            dropZone.appendChild(incoming);

            const finishSwap = (outcome = 'completed') => {
                if (token !== _sourcePreviewSwapToken) return;
                preview.src = nextSrc;
                preview.dataset.sourceId = toSourceId;
                _setDropBackdropImage(dropZone, nextSrc);
                applyKnownSizing({ animateResize: true });
                applyNaturalSizingFromImage(incoming, { animateResize: true });
                probeFullSourceDims({ animateResize: true });
                preview.classList.remove('img-loading');
                if (dropZone) dropZone.classList.remove('is-loading');
                _resetSourcePreviewSwap(preview);
                API.logUiFlow('main_photo', 'switch_animation', outcome, {
                    from_source_id: fromSourceId,
                    to_source_id: toSourceId,
                    duration_ms: SOURCE_PREVIEW_SWITCH_ANIM_MS,
                }).catch(() => {});
            };

            incoming.onload = () => {
                if (token !== _sourcePreviewSwapToken) return;
                API.logUiFlow('main_photo', 'switch_animation', 'started', {
                    from_source_id: fromSourceId,
                    to_source_id: toSourceId,
                    duration_ms: SOURCE_PREVIEW_SWITCH_ANIM_MS,
                }).catch(() => {});
                preview.classList.add('source-preview-swap-transition');
                preview.classList.add('source-preview-swap-out');
                window.requestAnimationFrame(() => {
                    if (token !== _sourcePreviewSwapToken) return;
                    incoming.classList.add('source-preview-swap-transition');
                    incoming.classList.add('source-preview-swap-in-active');
                    preview.classList.add('source-preview-swap-out-active');
                });
                _sourcePreviewSwapTimer = window.setTimeout(() => {
                    finishSwap('completed');
                }, SOURCE_PREVIEW_SWITCH_ANIM_MS + 24);
            };
            incoming.onerror = () => {
                if (token !== _sourcePreviewSwapToken) return;
                _resetSourcePreviewSwap(preview);
                API.logUiFlow('main_photo', 'switch_animation', 'fallback', {
                    from_source_id: fromSourceId,
                    to_source_id: toSourceId,
                }).catch(() => {});
                preview.classList.add('img-loading');
                if (dropZone) dropZone.classList.add('is-loading');
                preview.src = nextSrc;
                preview.dataset.sourceId = toSourceId;
                _setDropBackdropImage(dropZone, nextSrc);
                applyKnownSizing();
                if (shouldProbeFullDims) {
                    probeFullSourceDims();
                } else if (preview.complete && preview.naturalWidth && preview.naturalHeight) {
                    applyNaturalSizingFromImage(preview);
                    preview.classList.remove('img-loading');
                    if (dropZone) dropZone.classList.remove('is-loading');
                }
            };
            incoming.src = nextSrc;
        } else {
            _resetSourcePreviewSwap(preview);
            _setDropBackdropImage(dropZone, nextSrc);
            applyKnownSizing();
            if (currentSrc !== nextSrc) {
                if (nextSrc) {
                    preview.classList.add('img-loading');
                    if (dropZone) dropZone.classList.add('is-loading');
                }
                preview.src = nextSrc;
                preview.dataset.sourceId = toSourceId;
            } else if (nextSrc) {
                preview.dataset.sourceId = toSourceId;
            }
            if (shouldProbeFullDims) {
                probeFullSourceDims();
            }
            if (nextSrc && preview.complete && preview.naturalWidth && preview.naturalHeight) {
                applyNaturalSizingFromImage(preview);
                preview.classList.remove('img-loading');
                if (dropZone) dropZone.classList.remove('is-loading');
            }
        }
        preview.classList.toggle('hidden', !sourceUrl);
    }
    if (placeholder) {
        placeholder.classList.toggle('hidden', !!sourceUrl);
        if (!sourceUrl) {
            placeholder.innerHTML = _getMainPhotoPlaceholderHtml();
        }
    }
    if (overlay) {
        overlay.classList.toggle('hidden', !sourceUrl);
        overlay.innerHTML = '<span class="overlay-chip">↻ Change photo</span>';
    }
    if (!sourceUrl && dropZone) {
        _sourcePreviewSwapToken += 1;
        _resetSourcePreviewSwap(preview);
        _setDropBackdropImage(dropZone, '');
        _clearDropResizeAnimation(dropZone);
        dropZone.style.removeProperty('aspect-ratio');
        dropZone.style.removeProperty('--source-ratio');
        dropZone.classList.remove('is-loading');
    }
    if (!sourceUrl && preview) {
        preview.classList.remove('img-loading');
        preview.dataset.sourceId = '';
    }
    if (!sourceUrl) {
        setOriginalSize(0, 0);
    }
}

function _render() {
    renderRecentPhotos(state.sourcePhotos, {
        onSelect: selectRecentPhoto,
        onHide: hideRecentPhoto,
    });
    _setSourceWorkspaceVisibility();
    _refreshMainPhotoPlaceholderCopy();
}

function _emitCreateDraftDirty(reason = 'source') {
    document.dispatchEvent(new CustomEvent('workflow:draft_dirty', {
        detail: {
            reason: String(reason || 'source'),
            source_photo_id: String(state.selectedSourcePhotoId || '').trim(),
        },
    }));
}

function _applySelection(photo) {
    state.selectedSourcePhotoId = photo.id;
    state.selectedSourcePhoto = photo;
    state.targetJobId = photo.upload_job_id || photo.canonical_parent_job_id || '';
    state.jobId = state.targetJobId || state.jobId;
    state.targetFilename = photo.stored_filename || '';
    _setDropPreview(photo);
    setSettingsOverride(false);
    setRecentPhotoSelection(photo.id);
    refreshSettingsVisibility();
    _emitCreateDraftDirty('source_selected');
}

export function setSelectedSourceFromUpload(sourcePhoto, opts = {}) {
    if (!sourcePhoto || !sourcePhoto.id) return;
    const normalized = {
        ...sourcePhoto,
        has_been_used: Boolean(sourcePhoto.has_been_used),
        width: Number(sourcePhoto.width || 0),
        height: Number(sourcePhoto.height || 0),
        full_url: sourcePhoto.full_url || sourcePhoto.source_url || '',
        thumb_url: sourcePhoto.thumb_url || sourcePhoto.source_url || '',
    };
    _upsertSourcePhotoState(normalized);
    state.hasUploadedSourceThisSession = true;
    state.selectedSourcePhotoId = normalized.id;
    state.selectedSourcePhoto = normalized;
    state.targetJobId = normalized.upload_job_id || opts.jobId || '';
    state.jobId = state.targetJobId || opts.jobId || state.jobId;
    state.targetFilename = normalized.stored_filename || opts.filename || state.targetFilename;
    _setDropPreview(normalized);
    setSettingsOverride(false);
    upsertRecentPhoto(normalized, {
        onSelect: selectRecentPhoto,
        onHide: hideRecentPhoto,
    });
    _setSourceWorkspaceVisibility();
    setRecentPhotoSelection(normalized.id);
    refreshSettingsVisibility();
    _emitCreateDraftDirty('source_uploaded_selected');
    API.logUiFlow('main_photo', 'select_uploaded', 'ok', {
        source_id: normalized.id,
        dedup_hit: Boolean(opts && opts.dedupHit),
    }).catch(() => {});
}

export function markSourcePhotoUsed(sourcePhotoId) {
    const safeId = String(sourcePhotoId || '').trim();
    if (!safeId) return;
    const items = Array.isArray(state.sourcePhotos) ? [...state.sourcePhotos] : [];
    const idx = items.findIndex((p) => String(p.id || '') === safeId);
    if (idx < 0) return;

    const updated = { ...items[idx], has_been_used: true };
    items.splice(idx, 1);
    items.unshift(updated);
    state.sourcePhotos = items;

    if (String(state.selectedSourcePhotoId || '') === safeId) {
        state.selectedSourcePhoto = updated;
    }

    upsertRecentPhoto(updated, {
        onSelect: selectRecentPhoto,
        onHide: hideRecentPhoto,
    });
    setRecentPhotoSelection(state.selectedSourcePhotoId);
    _setSourceWorkspaceVisibility();
}

export function clearSelectedSourcePhoto() {
    state.selectedSourcePhotoId = null;
    state.selectedSourcePhoto = null;
    state.targetFilename = '';
    state.targetJobId = null;
    _setDropPreview('');
    const fileInput = document.getElementById('targetFile');
    if (fileInput) fileInput.value = '';
    setSettingsOverride(false);
    setRecentPhotoSelection(null);
    _setSourceWorkspaceVisibility();
    refreshSettingsVisibility();
    _emitCreateDraftDirty('source_cleared');
}

export async function loadRecentPhotos() {
    try {
        const photos = await API.getRecentPhotos(RECENT_PHOTOS_LIMIT);
        state.sourcePhotos = Array.isArray(photos)
            ? photos.map((p) => ({ ...p, has_been_used: Boolean(p.has_been_used) }))
            : [];

        if (state.selectedSourcePhotoId) {
            const selected = state.sourcePhotos.find((p) => p.id === state.selectedSourcePhotoId);
            if (!selected) {
                if (String(state.selectedSourcePhotoId).startsWith('legacy:')) {
                    _render();
                    return;
                }
                clearSelectedSourcePhoto();
                return;
            }
            state.selectedSourcePhoto = selected;
            _setDropPreview(selected);
        }
        _render();
    } catch (e) {
        state.sourcePhotos = [];
        _render();
    }
}

async function selectRecentPhoto(photo) {
    if (!photo || !photo.id) return;
    const nextId = String(photo.id || '');
    const currentId = state.selectedSourcePhotoId ? String(state.selectedSourcePhotoId) : '';
    if (currentId && currentId === nextId) {
        setRecentPhotoSelection(nextId);
        return;
    }
    _applySelection(photo);

    try {
        await API.logEvent('select_recent_photo', {
            source_id: photo.id,
            canonical_parent_id: photo.canonical_parent_id || '',
            has_been_used: Boolean(photo.has_been_used),
        });
        API.logUiFlow('main_photo', 'select_recent', 'ok', {
            source_id: photo.id,
            has_been_used: Boolean(photo.has_been_used),
        }).catch(() => {});
    } catch (e) {}
}

async function hideRecentPhoto(photo) {
    if (!photo || !photo.id) return;
    const ok = await openConfirmModal({
        title: 'Delete source photo?',
        message: 'Delete this source photo? Existing mosaics will still work.',
        confirmText: 'Delete',
        danger: true,
    });
    if (!ok) return;

    const deletingToast = showToast('Deleting main photo', 'Removing this photo…', 0);
    try {
        const result = await API.hideRecentPhoto(photo.id);
        state.sourcePhotos = (state.sourcePhotos || []).filter((item) => item.id !== photo.id);
        const fileInput = document.getElementById('targetFile');
        if (fileInput) fileInput.value = '';
        if (state.selectedSourcePhotoId === photo.id) {
            clearSelectedSourcePhoto();
            _render();
        } else {
            _render();
        }
        const activeJobs = Number(result && result.active_jobs ? result.active_jobs : 0);
        if (activeJobs > 0) {
            showToast('Main photo deleted', 'Removed from Uploaded Photos. Existing mosaics will still work.', 3200);
        } else {
            showToast('Main photo deleted', 'Photo removed from your uploads.', 2600);
        }
        API.logUiFlow('main_photo', 'delete', 'ok', {
            source_id: photo.id,
            active_jobs: activeJobs,
        }).catch(() => {});
    } catch (e) {
        showToast('Delete failed', `Failed to delete source photo: ${e.message || e}`, { duration: 0, priority: 'error' });
        API.logUiFlow('main_photo', 'delete', 'error', {
            source_id: photo.id,
        }).catch(() => {});
    } finally {
        if (deletingToast) deletingToast.close();
    }
}

export async function deleteSelectedSourcePhoto() {
    const photo = state.selectedSourcePhoto;
    if (!photo || !photo.id) return;
    await hideRecentPhoto(photo);
}

export function selectSourcePhotoById(photoId) {
    const safeId = String(photoId || '').trim();
    if (!safeId) return false;
    const match = (state.sourcePhotos || []).find((p) => String(p.id || '') === safeId);
    if (!match) return false;
    _applySelection(match);
    return true;
}

export function selectSourcePhotoByKey(sourceKey) {
    const safeKey = String(sourceKey || '').trim();
    if (!safeKey) return false;
    const match = (state.sourcePhotos || []).find((p) => String(p.source_key || '').trim() === safeKey);
    if (!match) return false;
    _applySelection(match);
    return true;
}

export function selectSourcePhotoByUrl(sourceUrl) {
    const safeUrl = String(sourceUrl || '').trim();
    if (!safeUrl) return false;
    const base = safeUrl.split('?')[0];
    const match = (state.sourcePhotos || []).find((p) => {
        const full = String(p.full_url || '').split('?')[0];
        const thumb = String(p.thumb_url || '').split('?')[0];
        return base && (base === full || base === thumb);
    });
    if (!match) return false;
    _applySelection(match);
    return true;
}
